/**
 * «Ролі» (/admin/roles) — РЕДИЗАЙН (VITE_REDESIGN).
 *
 * Прод без прапорця бачить незмінений RolesPageLegacy у page.tsx: маршрут не
 * гейтований жодним VITE_FEATURE_*, тож у прод-збірці сторінка присутня.
 *
 * Сторінка суто довідкова: жодного запиту, жодного збереження. Склад ролей,
 * їх порядок і ПЕРЕЛІК ДОЗВОЛІВ узято з ROLES_DEFINITION дослівно.
 *
 * Що виправлено:
 *  · власна палітра ролей (var(--accent) / #B8860B / #1F5B8F / #0D7A3C / #4A4A50 /
 *    #6B6B70) розходилася з /admin/users, де ті самі ролі вже на токенах —
 *    тепер обидві сторінки беруть кольори з одного джерела (ROLE_COLOR);
 *  · owner був червоним: §3 лишає брендовий червоний за реальною помилкою,
 *    а роль — не помилка;
 *  · картки мали бордер у колір ролі — тепер .tile, а роль позначає іконка
 *    та акцентна смуга;
 *  · Inter на h1 і 8 Remix-іконок — прибрано (§0);
 *  · смуга ієрархії була pill-кнопками з власною геометрією → SegmentedTabs,
 *    той самий компонент, що вкладки «Моніторинг обладнання» у SCADA.
 *
 * Описи ролей і формулювання дозволів лишені англійською ДОСЛІВНО — це
 * фактично документація прав, і її мова змінюється окремим рішенням.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import SegmentedTabs from '@/components/base/SegmentedTabs';
import {
  Check, Coins, Crown, Eye, Pencil, Settings, ShieldCheck, type LucideIcon,
} from 'lucide-react';

/**
 * Кольори ролей — ті самі, що в UsersRedesign: оператор → --ok, адміністратор
 * → --warn, власник → --series-3, решта нейтральні. Одна роль має виглядати
 * однаково на обох сторінках адміністрування.
 */
const ROLE_COLOR: Record<string, string> = {
  owner: 'var(--series-3)',
  admin: 'var(--warn)',
  operator: 'var(--ok)',
  editor: 'var(--series-2)',
  finance: 'var(--text-dim)',
  viewer: 'var(--text-dim)',
};

const ROLE_ICON: Record<string, LucideIcon> = {
  owner: Crown,
  admin: ShieldCheck,
  editor: Pencil,
  operator: Settings,
  finance: Coins,
  viewer: Eye,
};

/**
 * Зміст перенесено з ROLES_DEFINITION без правок: ті самі ролі, той самий
 * порядок, ті самі описи й ті самі дозволи.
 */
const ROLES_DEFINITION = [
  { role: 'owner', description: 'Full control over the tenant. Can manage all settings, users, and data.', permissions: ['Manage tenant settings', 'Add and remove users', 'Assign any role', 'Access all modules', 'Delete tenant data', 'Manage billing'] },
  { role: 'admin', description: 'Administrative access. Can manage users and most settings.', permissions: ['Manage users (except owner)', 'Assign roles up to admin', 'Access all modules', 'Manage configurations', 'View audit logs'] },
  { role: 'editor', description: 'Can create and edit records across all modules.', permissions: ['Create and edit counterparties', 'Create and edit assets', 'Manage contracts', 'Upload documents', 'Submit MMS reports'] },
  { role: 'operator', description: 'Operational access. Can manage assets and dispatch tasks.', permissions: ['Manage assets', 'Submit dispatch tasks', 'View forecasts', 'Read-only on contracts', 'Read-only on financials'] },
  { role: 'finance', description: 'Access to financial data, billing, and contracts.', permissions: ['View and export financial reports', 'Manage contracts', 'View billing', 'Read-only on assets', 'Read-only on forecasts'] },
  { role: 'viewer', description: 'Read-only access to all data.', permissions: ['View counterparties', 'View assets', 'View contracts', 'View documents', 'View forecasts'] },
];

export default function RolesRedesign() {
  const { t } = useTranslation();
  // Смуга ієрархії — не декор: вибір підсвічує відповідну картку, тож кнопки
  // мають реальну дію, а не імітують інтерактив.
  const [picked, setPicked] = useState<string>('');

  const roleLabel = (r: string) => t(`role_${r}`, { defaultValue: r });

  return (
    <DashboardLayout breadcrumb={[t('nav_set_roles')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className="px-4 md:px-6 pt-5 pb-10" style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>

          {/* Заголовок у тілі не дублюємо — назву розділу показує топбар. */}

          {/* Ієрархія — той самий SegmentedTabs, що вкладки «Моніторинг
              обладнання» у SCADA: пілюля 36/10, елементи 28/8, тонка обводка. */}
          <div className="tile p-4 sm:p-5 mb-3">
            <div className="text-[10.5px] font-bold uppercase mb-3"
              style={{ color: 'var(--text-dim)', letterSpacing: '0.06em' }}>
              {t('roles_hierarchy')}
            </div>
            <SegmentedTabs
              items={ROLES_DEFINITION.map((d) => ({
                key: d.role,
                label: roleLabel(d.role),
                Icon: ROLE_ICON[d.role],
              }))}
              activeKey={picked}
              onSelect={(k) => setPicked((prev) => (prev === k ? '' : k))}
            />
            <p className="text-[11px] mt-3" style={{ color: 'var(--text-muted)' }}>
              {t('roles_hierarchy_note')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {ROLES_DEFINITION.map((def) => {
              const color = ROLE_COLOR[def.role] ?? 'var(--text-dim)';
              const Icon = ROLE_ICON[def.role] ?? Eye;
              const on = picked === def.role;
              return (
                <div key={def.role} className="tile p-4 sm:p-5 transition-colors"
                  style={{ borderLeft: `3px solid ${on ? color : 'transparent'}` }}>
                  <div className="flex items-center gap-2.5 mb-3">
                    <Icon size={17} strokeWidth={1.9} style={{ color, flexShrink: 0 }} />
                    <div className="min-w-0">
                      <div className="font-display text-[13.5px] font-semibold" style={{ color: 'var(--text)' }}>
                        {roleLabel(def.role)}
                      </div>
                      <div className="text-[10.5px]" style={{ color: 'var(--text-muted)' }}>
                        {def.permissions.length} {t('roles_permissions')}
                      </div>
                    </div>
                  </div>

                  {/* Опис — англійською дослівно, як у вихідній версії. */}
                  <p className="text-[11.5px] leading-relaxed pb-3 mb-3"
                    style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--line)' }}>
                    {def.description}
                  </p>

                  <div className="flex flex-col gap-1.5">
                    {def.permissions.map((perm) => (
                      <div key={perm} className="flex items-start gap-2 text-[11.5px]" style={{ color: 'var(--text)' }}>
                        <Check size={13} strokeWidth={2.4} style={{ color, marginTop: 1, flexShrink: 0 }} />
                        <span>{perm}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
